import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Component from '../../components/Component';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    getFanAgentHealth,
    getFanAgentLive,
    getFanAgentPosts,
} from '../../services/fanAgentAdminApi';

const AGENTS = [
    { id: '', label: 'All' },
    { id: 'loyal_stump', label: '@LoyalStump' },
    { id: 'whistle_podu', label: '@WhistlePodu' },
    { id: 'cric_judge', label: '@CricJudge' },
];

const SCENARIOS = [
    { id: '', label: 'All scenarios' },
    { id: 'total_domination', label: 'Total domination' },
    { id: 'going_well', label: 'Going well' },
    { id: 'under_pressure', label: 'Under pressure' },
    { id: 'news', label: 'News' },
    { id: 'toss', label: 'Toss' },
];

const STATUS_CHIP = {
    ready: { label: 'Ready', bg: '#E8F5E9', color: '#1B5E20' },
    climbing: { label: 'Climbing', bg: '#E3F2FD', color: '#0D47A1' },
    paced: { label: 'Paced', bg: '#FFF8E1', color: '#E65100' },
    capped: { label: 'Capped', bg: '#EEEEEE', color: '#424242' },
    posted: { label: 'Posted', bg: '#F3E5F5', color: '#6A1B9A' },
    open: { label: 'News open', bg: '#E0F2F1', color: '#00695C' },
};

const STALL_MS = 15 * 60 * 1000;
const BOWLING_MILESTONES = new Set(['3fer', '5fer', 'control', 'lone_bowler']);

const AFFILIATION = {
    FAV_FRANCHISE: 'Favourite',
    RIVAL_FRANCHISE: 'Rival',
    NEUTRAL: 'Neutral',
    MERIT: 'Merit',
};

function todayIst() {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

function clockIst(iso) {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(date);
}

function dateTimeIst(iso) {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(date);
}

function durationSec(start, end) {
    if (!start || !end) return null;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    return Math.round(ms / 1000);
}

function scenarioLabel(id) {
    return SCENARIOS.find((row) => row.id === id)?.label || id || '—';
}

function lastTickAt(tick) {
    const finished = tick?.finished_at ? new Date(tick.finished_at).getTime() : NaN;
    const started = tick?.started_at ? new Date(tick.started_at).getTime() : NaN;
    if (Number.isFinite(finished)) return finished;
    if (Number.isFinite(started)) return started;
    return null;
}

function isBattingCard(item) {
    return item?.scenario !== 'toss' && !BOWLING_MILESTONES.has(item?.milestone) && Number(item?.balls) > 0;
}

function countdownLabel(seconds) {
    const s = Math.max(0, seconds || 0);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
}

function frozenLine(item) {
    if (item.scenario === 'toss') return item.line || 'Toss';
    const bits = [item.line || `${item.runs} off ${item.balls}`];
    if (item.strike_rate != null && isBattingCard(item)) bits.push(`SR ${item.strike_rate}`);
    if (item.team_score) bits.push(item.team_score);
    if (item.required_rate != null) bits.push(`chase RRR ${item.required_rate}`);
    return bits.join(' · ');
}

function StatusChip({ status }) {
    const tone = STATUS_CHIP[status] || { label: status || '—', bg: '#EEEEEE', color: '#424242' };
    return (
        <Chip
            label={tone.label}
            size="small"
            sx={{ bgcolor: tone.bg, color: tone.color, fontWeight: 700 }}
        />
    );
}

function HealthStrip({ health, secondsLeft, stalled }) {
    const tick = health?.last_tick;
    const duration = durationSec(tick?.started_at, tick?.finished_at);
    const bg = stalled ? '#FFF3E0' : '#F7F7F5';
    const border = stalled ? '#E65100' : '#E7E5E4';
    return (
        <Box sx={{ p: 2, mb: 2, bgcolor: bg, border: `1px solid ${border}`, borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700}>Health</Typography>
                {stalled && <Chip label="STALLED" size="small" sx={{ bgcolor: '#E65100', color: '#fff', fontWeight: 700 }} />}
                <Chip
                    label={health?.cron_enabled ? 'Cron on' : 'Cron off'}
                    size="small"
                    sx={{ fontWeight: 700, bgcolor: health?.cron_enabled ? '#E8F5E9' : '#FFEBEE', color: health?.cron_enabled ? '#1B5E20' : '#B71C1C' }}
                />
                <Typography variant="caption" color="text.secondary">{health?.cron_expr} · {health?.timezone}</Typography>
            </Stack>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">Next tick</Typography>
                    <Typography variant="h6" fontWeight={700}>{clockIst(health?.next_tick_ist)} IST</Typography>
                    <Typography variant="body2">in {countdownLabel(secondsLeft)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">Last tick</Typography>
                    {tick ? (
                        <>
                            <Typography variant="body2" fontWeight={700}>
                                Start {dateTimeIst(tick.started_at)} IST
                            </Typography>
                            <Typography variant="body2">
                                Finish {tick.finished_at ? `${dateTimeIst(tick.finished_at)} IST` : 'still running'}
                                {duration != null ? ` · ${duration}s` : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {tick.matches_seen} matches
                                {' · '}{tick.candidates} candidates
                                {' · '}{tick.created} written
                            </Typography>
                            {tick.error && <Typography variant="body2" color="error">{tick.error}</Typography>}
                        </>
                    ) : (
                        <Typography variant="body2">No tick recorded yet.</Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">Today</Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {(health?.agents || []).map((agent) => (
                            <Typography key={agent.id} variant="body2">
                                <b>{agent.handle}</b> {agent.posts_today} of {agent.pace_cap} pace · max {agent.daily_max}
                            </Typography>
                        ))}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}

function PlayCard({ item }) {
    return (
        <Card variant="outlined" sx={{ mb: 1.25 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">{item.handle}</Typography>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {item.player_name}
                            {item.team ? ` · ${item.team}` : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{item.match_title || item.match_id}</Typography>
                        {item.affiliation && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                                {AFFILIATION[item.affiliation] || item.affiliation}
                            </Typography>
                        )}
                    </Box>
                    <StatusChip status={item.status} />
                </Stack>
                <Typography variant="body2" sx={{ mt: 1, fontFamily: 'ui-monospace, monospace' }}>
                    {frozenLine(item)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                    <b>{item.pointer}</b> / 100
                    {item.gate != null ? ` · gate ${item.gate}` : ''}
                    {item.scenario ? ` · ${scenarioLabel(item.scenario)}` : ''}
                    {item.milestone && item.milestone !== item.scenario ? ` · ${item.milestone}` : ''}
                    {isBattingCard(item) && item.runs_to_fifty > 0 ? ` · ${item.runs_to_fifty} to fifty` : ''}
                    {isBattingCard(item) && item.runs_to_hundred > 0 ? ` · ${item.runs_to_hundred} to hundred` : ''}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>{item.eta}</Typography>
            </CardContent>
        </Card>
    );
}

function FanAgents() {
    const [agentId, setAgentId] = useState('');
    const [scenario, setScenario] = useState('');
    const [day, setDay] = useState(todayIst);
    const [health, setHealth] = useState(null);
    const [live, setLive] = useState(null);
    const [posts, setPosts] = useState([]);
    const [healthError, setHealthError] = useState('');
    const [liveError, setLiveError] = useState('');
    const [postsError, setPostsError] = useState('');
    const [postsLoading, setPostsLoading] = useState(true);
    const [liveLoading, setLiveLoading] = useState(true);
    const [secondsLeft, setSecondsLeft] = useState(null);
    const [selected, setSelected] = useState(null);

    const loadHealth = useCallback(async () => {
        try {
            const data = await getFanAgentHealth();
            setHealth(data);
            setHealthError('');
            if (typeof data?.seconds_until_tick === 'number') setSecondsLeft(data.seconds_until_tick);
        } catch (err) {
            setHealthError(err.message || 'Failed to load health');
        }
    }, []);

    const loadLive = useCallback(async () => {
        try {
            const data = await getFanAgentLive();
            setLive(data);
            setLiveError('');
        } catch (err) {
            setLiveError(err.message || 'Failed to load the live queue');
        } finally {
            setLiveLoading(false);
        }
    }, []);

    const loadPosts = useCallback(async () => {
        setPostsLoading(true);
        try {
            const data = await getFanAgentPosts({ day, agentId, scenario });
            setPosts(data?.posts || []);
            setPostsError('');
        } catch (err) {
            setPostsError(err.message || 'Failed to load posts');
        } finally {
            setPostsLoading(false);
        }
    }, [day, agentId, scenario]);

    useEffect(() => { loadHealth(); loadLive(); }, [loadHealth, loadLive]);
    useEffect(() => { loadPosts(); }, [loadPosts]);

    useEffect(() => {
        const poll = setInterval(() => {
            loadHealth();
            loadLive();
        }, 30000);
        return () => clearInterval(poll);
    }, [loadHealth, loadLive]);

    useEffect(() => {
        const tick = setInterval(() => {
            setSecondsLeft((prev) => (prev == null ? prev : Math.max(0, prev - 1)));
        }, 1000);
        return () => clearInterval(tick);
    }, []);

    const stalled = useMemo(() => {
        if (!health?.cron_enabled) return false;
        const at = lastTickAt(health?.last_tick);
        if (at == null) return true;
        return Date.now() - at > STALL_MS;
    }, [health]);

    const items = useMemo(() => {
        const rows = live?.items || [];
        if (!agentId) return rows;
        return rows.filter((row) => row.agent_id === agentId);
    }, [live, agentId]);

    const news = useMemo(() => {
        const rows = live?.news || [];
        if (!agentId) return rows;
        return rows.filter((row) => row.agent_id === agentId);
    }, [live, agentId]);

    const nextLabel = clockIst(live?.next_tick_ist || health?.next_tick_ist);

    return (
        <Component>
            <div className="page-body">
                <div className="container-fluid">
                    <Box sx={{ py: 2 }}>
                        <Typography variant="h5" fontWeight={700}>Fan agents</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Is the agent working, what it has posted, and what it is about to post. Live queue refreshes every 30 seconds.
                        </Typography>

                        {healthError && <Alert severity="error" sx={{ mb: 2 }}>{healthError}</Alert>}
                        {health && (
                            <HealthStrip health={health} secondsLeft={secondsLeft} stalled={stalled} />
                        )}

                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            {AGENTS.map((agent) => {
                                const on = agentId === agent.id;
                                return (
                                    <Chip
                                        key={agent.label}
                                        label={agent.label}
                                        onClick={() => setAgentId(agent.id)}
                                        sx={{
                                            fontWeight: 700,
                                            bgcolor: on ? '#1C1917' : '#fff',
                                            color: on ? '#fff' : '#1C1917',
                                            border: '1px solid #E7E5E4',
                                        }}
                                    />
                                );
                            })}
                        </Stack>

                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={7}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>In play</Typography>
                                {liveError && <Alert severity="error" sx={{ mb: 1 }}>{liveError}</Alert>}
                                {liveLoading && !live && <CircularProgress size={22} />}
                                {!liveLoading && !liveError && items.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        No live match the agents are watching. Next tick {nextLabel} IST.
                                    </Typography>
                                )}
                                {items.map((item) => (
                                    <PlayCard
                                        key={`${item.agent_id}:${item.match_id}:${item.player_name}:${item.innings}`}
                                        item={item}
                                    />
                                ))}
                                {news.map((row) => (
                                    <Card key={`news:${row.agent_id}`} variant="outlined" sx={{ mb: 1.25, bgcolor: '#FAFAF9' }}>
                                        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">{row.handle} · News</Typography>
                                                    <Typography variant="body2">{row.eta}</Typography>
                                                </Box>
                                                <StatusChip status={row.status} />
                                            </Stack>
                                            {row.feed_id != null && (
                                                <Typography variant="caption" color="text.secondary">Feed #{row.feed_id}</Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </Grid>

                            <Grid item xs={12} md={5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={700}>Created</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                                    <TextField
                                        label="Day (IST)"
                                        type="date"
                                        size="small"
                                        value={day}
                                        onChange={(e) => setDay(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 160 }}
                                    />
                                    <TextField
                                        select
                                        label="Scenario"
                                        size="small"
                                        value={scenario}
                                        onChange={(e) => setScenario(e.target.value)}
                                        sx={{ minWidth: 180 }}
                                    >
                                        {SCENARIOS.map((row) => (
                                            <MenuItem key={row.id || 'all'} value={row.id}>{row.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                                {postsError && <Alert severity="error" sx={{ mb: 1 }}>{postsError}</Alert>}
                                {postsLoading && <CircularProgress size={22} />}
                                {!postsLoading && !postsError && posts.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        {day === todayIst() ? 'No posts yet today.' : 'No posts on this day.'}
                                    </Typography>
                                )}
                                {posts.length > 0 && (
                                    <Box sx={{ overflowX: 'auto', border: '1px solid #E7E5E4', borderRadius: 1 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Time</TableCell>
                                                    <TableCell>Agent</TableCell>
                                                    <TableCell>Player</TableCell>
                                                    <TableCell>Match</TableCell>
                                                    <TableCell>Scenario</TableCell>
                                                    <TableCell>Pointer</TableCell>
                                                    <TableCell>Post</TableCell>
                                                    <TableCell>Feed</TableCell>
                                                    <TableCell>Likes</TableCell>
                                                    <TableCell>Comments</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {posts.map((post) => (
                                                    <TableRow
                                                        key={post.post_id}
                                                        hover
                                                        onClick={() => setSelected(post)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeIst(post.created_at)}</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{post.handle}</TableCell>
                                                        <TableCell>{post.player_name || '—'}</TableCell>
                                                        <TableCell>{post.match_title || post.match_id || '—'}</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{scenarioLabel(post.scenario)}</TableCell>
                                                        <TableCell>{post.pointer ?? '—'}</TableCell>
                                                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {post.text}
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            {post.feed_id == null ? 'not on the feed' : `#${post.feed_id}`}
                                                        </TableCell>
                                                        <TableCell>{post.likes == null ? '—' : post.likes}</TableCell>
                                                        <TableCell>{post.comments == null ? '—' : post.comments}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </div>
            </div>

            <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selected?.handle}{selected?.player_name ? ` · ${selected.player_name}` : ''}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{selected?.text}</Typography>
                    <Typography variant="caption" color="text.secondary">Frozen scorecard</Typography>
                    <Box component="pre" sx={{ mt: 0.5, p: 1.5, bgcolor: '#F7F7F5', overflow: 'auto', fontSize: 12 }}>
                        {selected?.frozen_scorecard
                            ? JSON.stringify(selected.frozen_scorecard, null, 2)
                            : 'No frozen scorecard on this post.'}
                    </Box>
                </DialogContent>
            </Dialog>
        </Component>
    );
}

export default FanAgents;
